import React from "react";

import { Link } from "@remix-run/react";

import { cn } from "~/lib/utils";
import { Badge } from "~/components/ui/badge";
import { RadioCard } from "~/components/ui/radio-card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/ui/select";
import { useUserDetails } from "~/hooks/queries/use-user-details";
import { getFullDisplayName } from "~/lib/display-name";
import { logger } from "~/lib/logger";
import { useUserProfile } from "~/lib/user";

import { Alert, AlertDescription } from "../ui/alert";
import { H3, P } from "../ui/typography";

interface ClientSelectorProps {
    selectedClientId: string | null;
    onChange: (clientId: string, clientEmail?: string | null) => void;
    variant?: "radio" | "select";
    justClients?: boolean;
    label?: string;
    selectAlign?: "start" | "center" | "end";
    triggerClassName?: string;
}

export const ClientSelector: React.FC<ClientSelectorProps> = ({
    selectedClientId,
    onChange,
    variant = "radio",
    justClients = false,
    label = "Who is this session with?",
    selectAlign,
    triggerClassName,
}) => {
    const { data, isLoading, error } = useUserDetails();
    const { data: userProfile } = useUserProfile();

    if (isLoading) return <></>;
    if (error) {
        logger
            .withError(error as Error)
            .error("Failed to load user relationships for ClientSelector");
        return <></>;
    }

    if (!data?.isPractitioner) {
        return null;
    }

    // AIDEV-NOTE: Show all clients (including pending) to practitioners so they can assign sessions before client accepts
    const allClients = data.clients;

    if (variant === "select") {
        const selectValue =
            selectedClientId === userProfile?.id
                ? "self"
                : selectedClientId ?? "other";

        // AIDEV-NOTE: Helper to get email for selected client
        const getClientEmail = (value: string): string | null => {
            if (value === "self") return userProfile?.email ?? null;
            if (value === "other") return null;
            const client = allClients.find((c) => c.id === value);
            return client?.email ?? null;
        };

        return (
            <Select
                value={selectValue}
                onValueChange={(value) => {
                    logger
                        .withMetadata({ clientId: value })
                        .info("Upload: client selected");
                    onChange(value, getClientEmail(value));
                }}
            >
                <SelectTrigger
                    className={cn("w-full", triggerClassName)}
                    id="client-selector"
                >
                    <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent align={selectAlign}>
                    {allClients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                            {/* AIDEV-NOTE: Preserve full names to disambiguate same-first-name clients in add-session flows */}
                            {getFullDisplayName(client, "Unknown Client")}
                        </SelectItem>
                    ))}
                    {userProfile?.id && (
                        <SelectItem value="self">I'm the client</SelectItem>
                    )}
                    <SelectItem value="other">Other</SelectItem>
                </SelectContent>
            </Select>
        );
    }

    const getCheckedState = (value: string | null) => {
        if (value === "other") {
            return !selectedClientId || selectedClientId === "other";
        }
        if (value === "self") {
            return (
                selectedClientId === "self" ||
                selectedClientId === userProfile?.id
            );
        }
        return selectedClientId === value;
    };

    // AIDEV-NOTE: Show invite message if practitioner has no clients at all
    const showClientInviteMessage =
        data?.isPractitioner && allClients.length === 0;

    return (
        <div>
            <H3 className="font-medium text-brand-forest-800">{label}</H3>
            {showClientInviteMessage && (
                <Alert className="mb-3">
                    <AlertDescription>
                        <P className="text-sm">
                            Ready to share sessions with your clients?{" "}
                            <Link
                                to="/practitioner/clients"
                                className="font-medium text-brand-terra-500 underline hover:text-brand-terra-600"
                            >
                                Invite your first client here
                            </Link>
                            .
                        </P>
                    </AlertDescription>
                </Alert>
            )}
            <div className="grid grid-cols-1 gap-3">
                {allClients.map((client) => (
                    <RadioCard
                        key={client.id}
                        labelClassName="text-sm font-normal"
                        label={getFullDisplayName(client, "Unknown Client")}
                        value={client.id}
                        name={`client-${client.id}`}
                        checked={getCheckedState(client.id)}
                        onChange={() => {
                            logger
                                .withMetadata({ clientId: client.id })
                                .info("Upload: client selected");
                            onChange(client.id, client.email);
                        }}
                    />
                ))}
                {userProfile?.id && !justClients && (
                    <RadioCard
                        labelClassName="text-sm font-normal"
                        label="I'm the client"
                        value="self"
                        name="client-self"
                        checked={getCheckedState("self")}
                        onChange={() => {
                            logger
                                .withMetadata({ clientId: "self" })
                                .info("Upload: client selected");
                            onChange("self", userProfile?.email);
                        }}
                    />
                )}
                <RadioCard
                    labelClassName="text-sm font-normal"
                    label="Other"
                    value="other"
                    name="client-other"
                    checked={getCheckedState("other")}
                    onChange={() => {
                        logger
                            .withMetadata({ clientId: "other" })
                            .info("Upload: client selected");
                        onChange("other", null);
                    }}
                />
            </div>
        </div>
    );
};
